import { z } from 'zod';

export const userStatusEnum = z.enum(['active', 'suspended', 'pending', 'invited']);

export const createUserSchema = z.object({
  email: z.string().email('อีเมลไม่ถูกต้อง').max(255),
  username: z
    .string()
    .min(3, 'username อย่างน้อย 3 ตัว')
    .max(64)
    .regex(/^[a-z0-9._-]+$/i, 'username ใช้ได้แค่ a-z 0-9 . _ -'),
  name: z.string().min(1, 'กรุณาใส่ชื่อ').max(100),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัว').max(128),
  roleId: z.string().uuid(),
  status: userStatusEnum.default('active'),
  bio: z.string().max(500).optional().nullable(),
  avatarUrl: z.string().url().max(500).optional().nullable(),
});

export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  email: z.string().email().max(255).optional(),
  username: z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9._-]+$/i)
    .optional(),
  name: z.string().min(1).max(100).optional(),
  // Optional new password — only set when caller wants a reset.
  password: z.string().min(8).max(128).optional(),
  roleId: z.string().uuid().optional(),
  status: userStatusEnum.optional(),
  bio: z.string().max(500).optional().nullable(),
  avatarUrl: z.string().url().max(500).optional().nullable(),
});

export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const listUsersQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().positive().max(100).default(20),
  status: userStatusEnum.optional(),
  q: z.string().trim().min(1).max(255).optional(),
  roleId: z.string().uuid().optional(),
  /** Filter by TOTP enrollment status.
   *  - `on` = enrolled
   *  - `off` = not enrolled (anyone)
   *  - `required-missing` = role appears in the `auth.required_2fa_roles`
   *    setting AND user has no 2FA. Tighter signal than `off` — only
   *    flags users who *should* have it. Empty setting → no matches. */
  twoFactor: z.enum(['on', 'off', 'required-missing']).optional(),
});

export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

/** Full-replacement payload for the role × permission editor. */
export const updateRolePermissionsSchema = z.object({
  permissionCodes: z.array(z.string().min(1).max(64)).max(200),
});

export type UpdateRolePermissionsInput = z.infer<typeof updateRolePermissionsSchema>;
